import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { clearAuthSessionMarkers } from "../../utils/authUtils";
import { useTheme } from "../../contexts/ThemeContext";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Menu,
  CircleUser,
  LogOut,
  Sun,
  Moon,
  HelpCircle,
  User,
  Building2,
  Shield,
} from "lucide-react";
import { GlobalSearch } from "./GlobalSearch";
import HotelSwitcher from "./HotelSwitcher";
import LanguageSwitcher from "../shared/LanguageSwitcher";
import { AudioNotificationToggle } from "./layout/AudioNotificationToggle";
import { useHotel } from "../../contexts/HotelContext";

interface TopbarProps {
  onMenuClick: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ onMenuClick }) => {
  const [userName, setUserName] = useState<string>("User");
  const { isAuthenticated, user, logout: auth0Logout, isLoading } = useAuth0();
  const { t } = useTranslation();
  const { toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { isPlatformAdmin, myHotelInfo } = useHotel();

  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      setUserName(
        user.name || user.nickname || user.email || "Authenticated User",
      );
    } else if (!isLoading && !isAuthenticated) {
      setUserName("Guest");
    }
  }, [isLoading, isAuthenticated, user]);

  const handleLogout = () => {
    clearAuthSessionMarkers();
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-2 sm:gap-4 bg-background/95 backdrop-blur-xs border-b border-border/60 px-3 sm:px-4 lg:px-8">
      {/* Mobile menu button - visible below lg to match sidebar's lg:static breakpoint */}
      <Button
        size="icon"
        variant="outline"
        className="lg:hidden"
        onClick={onMenuClick}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle menu</span>
      </Button>

      {/* Hotel Switcher - shown when user has multiple hotels */}
      <div className="max-w-[120px] sm:max-w-none truncate">
        <HotelSwitcher />
      </div>

      {/* Support Mode indicator for platform admins */}
      {isPlatformAdmin && myHotelInfo?.hotelName && (
        <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs font-medium">
          <Shield className="h-3 w-3" />
          <span>{t("platform_admin_support_mode", "Support Mode")}</span>
        </div>
      )}

      {/* Global Search */}
      <div className="ml-auto flex-1 max-w-md">
        <GlobalSearch
          placeholder={t(
            "admin_search_placeholder",
            "Search orders, rooms, guests...",
          )}
        />
      </div>

      {/* Language switcher - hidden on mobile, shown in user menu instead */}
      <div className="hidden md:flex">
        <LanguageSwitcher />
      </div>

      {/* Audio notifications toggle - hidden on mobile */}
      <div className="hidden md:flex">
        <AudioNotificationToggle />
      </div>

      {/* Theme toggle */}
      <Button variant="ghost" size="icon" onClick={toggleTheme}>
        <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        <span className="sr-only">Toggle theme</span>
      </Button>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="overflow-hidden">
            <CircleUser className="h-5 w-5" />
            <span className="sr-only">User menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{user?.email || userName}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* Mobile-only: Language & Audio controls */}
          <div className="md:hidden px-2 py-1.5 flex items-center gap-2">
            <LanguageSwitcher />
            <AudioNotificationToggle />
          </div>
          <div className="md:hidden">
            <DropdownMenuSeparator />
          </div>
          <DropdownMenuItem onClick={() => navigate("/admin/account")}>
            <User className="mr-2 h-4 w-4" />
            <span>{t("admin_user_menu_account_settings", "Account settings")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
            <Building2 className="mr-2 h-4 w-4" />
            <span>{t("admin_user_menu_hotel_settings", "Hotel settings")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/admin/support")}>
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>{t("admin_user_menu_support", "Support")}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t("admin_user_menu_logout", "Logout")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};

export default Topbar;
